import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const CONSENT_TEXT =
  "Acconsento al trattamento del mio indirizzo email per ricevere la newsletter di Smart News Italia. Ho letto la Privacy Policy e posso revocare il consenso in qualsiasi momento.";

export default function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [consentError, setConsentError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (!consent) {
      setConsentError("Per iscriverti devi selezionare la casella di consenso qui sotto.");
      return;
    }
    setConsentError("");
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email, name, consent: true, consent_text: CONSENT_TEXT });
      toast.success(data.message);
      setEmail("");
      setName("");
      setConsent(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative bg-primary text-primary-foreground rounded-sm overflow-hidden grain">
      <div className="relative z-10 px-6 sm:px-12 py-14 max-w-3xl">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold opacity-80 mb-4">
          <Mail className="h-4 w-4" /> Newsletter
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl font-bold leading-tight mb-4">
          Discorsi tradotti, ogni settimana nella tua email
        </h2>
        <p className="text-primary-foreground/80 mb-4 leading-relaxed">
          Ogni settimana ricevi per email una selezione di discorsi, dichiarazioni e conferenze
          stampa dal mondo, tradotti in italiano con video originale, testo integrale e fonte
          ufficiale.
        </p>

        {/* Informativa vicino ai campi */}
        <p className="text-xs text-primary-foreground/70 mb-4 leading-relaxed max-w-xl">
          I dati inseriti (nome facoltativo ed email) sono trattati solo per l'invio della newsletter
          e per personalizzare il messaggio (double opt-in: riceverai un'email di conferma). Consulta
          l'<Link to="/privacy-policy" className="underline hover:text-white">Informativa privacy</Link>.
        </p>

        <form onSubmit={submit} className="max-w-md">
          <Input
            type="text"
            data-testid="newsletter-name-input"
            placeholder="Il tuo nome (facoltativo)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-sm h-11 mb-3"
          />
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="email"
              required
              data-testid="newsletter-email-input"
              placeholder="Il tuo indirizzo email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-sm h-11"
            />
            <Button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit-btn"
              className="bg-white text-primary hover:bg-white/90 rounded-sm h-11 gap-1.5 font-semibold shrink-0"
            >
              Iscriviti alla newsletter <ArrowRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Consenso non preselezionato — checkbox nativa con label associata */}
          <div className="flex items-start gap-3 mt-4">
            <input
              type="checkbox"
              id="newsletter-consent"
              checked={consent}
              onChange={(e) => { setConsent(e.target.checked); if (e.target.checked) setConsentError(""); }}
              aria-invalid={consentError ? "true" : "false"}
              aria-describedby={consentError ? "newsletter-consent-error" : undefined}
              data-testid="newsletter-consent-checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-white"
            />
            <label
              htmlFor="newsletter-consent"
              className="text-xs text-primary-foreground/85 leading-relaxed cursor-pointer"
            >
              Acconsento al trattamento del mio indirizzo email per ricevere la newsletter di Smart
              News Italia. Ho letto la{" "}
              <Link to="/privacy-policy" className="underline hover:text-white">Privacy Policy</Link>{" "}
              e posso revocare il consenso in qualsiasi momento.
            </label>
          </div>
          {consentError && (
            <p
              id="newsletter-consent-error"
              role="alert"
              data-testid="newsletter-consent-error"
              className="text-xs text-white font-semibold mt-2"
            >
              {consentError}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}
